import { LegalEntityCountry, TermsAndConditionsCountry } from '@seapoint/server/enums';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { debounce } from 'lodash';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import useSWRImmutable from 'swr/immutable';
import { useCompanyAPI } from '../../api/use-company-api';
import { useUserInfo } from '../../contexts/user-context/use-user-info';
import { Checkbox } from '../../design-system/checkbox/checkbox';
import { Inline } from '../../design-system/inline/inline';
import { CompanyView, TermsAndConditionsView } from '../../types/entity-types';

const CreateCompany = () => {
  // Feature flags
  const { allowGermanyCompanyCreation } = useFlags();

  // The main input fields on the form.
  const [country, setCountry] = useState<LegalEntityCountry | undefined>(LegalEntityCountry.IE);
  const termsAndConditionsCountry = useMemo(() => {
    return country === LegalEntityCountry.GB ? TermsAndConditionsCountry.GB : TermsAndConditionsCountry.EU;
  }, [country]);
  const termsAndConditionsUrl = useMemo(() => {
    return country === LegalEntityCountry.GB
      ? 'https://www.seapoint.co/terms-and-conditions'
      : 'https://www.seapoint.co/eu-terms-and-conditions';
  }, [country]);

  // The company search fields.
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState<CompanyView[] | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyView | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);

  // Manual entry fields for countries without company lookup
  const [manualCompanyName, setManualCompanyName] = useState('');
  const [manualCompanyType, setManualCompanyType] = useState('');
  const [manualRegistrationNo, setManualRegistrationNo] = useState('');
  const [manualStreet, setManualStreet] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [manualCounty, setManualCounty] = useState('');
  const [manualPostalCode, setManualPostalCode] = useState('');

  // Determine if the selected country requires company lookup
  const requiresCompanyLookup = useMemo(() => {
    return country !== LegalEntityCountry.DE;
  }, [country]);

  const { data: termsAndConditions } = useSWRImmutable<TermsAndConditionsView>(
    country ? `/api/terms-and-conditions/${termsAndConditionsCountry}/latest` : null,
  );

  const isContinueDisabled = useMemo(() => {
    if (!country || !hasAgreedToTerms || !termsAndConditions) {
      return true;
    }

    if (requiresCompanyLookup) {
      return !selectedCompany;
    }

    // For manual entry (Germany), require all fields
    return (
      !manualCompanyName ||
      !manualCompanyType ||
      !manualRegistrationNo ||
      !manualStreet ||
      !manualCity ||
      !manualPostalCode
    );
  }, [
    country,
    hasAgreedToTerms,
    termsAndConditions,
    requiresCompanyLookup,
    selectedCompany,
    manualCompanyName,
    manualCompanyType,
    manualRegistrationNo,
    manualStreet,
    manualCity,
    manualPostalCode,
  ]);

  // API hooks for communication with the backend.
  const { searchCompanies, createCompany } = useCompanyAPI();
  const { authenticatedInfo, mutations: userInfoMutations } = useUserInfo();

  // New state for error handling
  const [error, setError] = useState<string | null>(null);

  // Move the debounce outside of useCallback
  const performSearch = useCallback(
    async (query: string) => {
      if (query.length > 3) {
        setIsSearching(true);
        try {
          const results = await searchCompanies(query, country);
          setCompanyResults(results);
        } catch {
          toast.error('There was a problem searching your company');
        } finally {
          setIsSearching(false);
        }
      }
    },
    [country, searchCompanies],
  );

  // Update debouncedSearch to depend on performSearch
  const debouncedSearch = useMemo(() => debounce(performSearch, 300), [performSearch]);

  // Clean up effect
  useEffect(() => {
    if (companySearch) {
      debouncedSearch(companySearch);
    }

    return () => {
      debouncedSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySearch]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); // Clear any previous errors

    if (!country) {
      // This should never happen because the country is required to submit the form
      // See isContinueDisabled
      return null;
    }

    if (!termsAndConditions) {
      // This should never happen because the terms and conditions are required to submit the form
      throw new Error('No terms and conditions to create company');
    }

    try {
      let companyData: Partial<CompanyView>;

      if (requiresCompanyLookup) {
        if (!selectedCompany) {
          throw new Error('No company data to create');
        }
        companyData = selectedCompany;
      } else {
        // Manual entry for countries without lookup (e.g., Germany)
        companyData = {
          legalEntity: {
            name: manualCompanyName,
            type: manualCompanyType,
            registrationNo: manualRegistrationNo,
            country,
          },
          registeredAddress: {
            street: manualStreet,
            city: manualCity,
            county: manualCounty,
            postalCode: manualPostalCode,
            country,
          },
        };
      }

      try {
        await createCompany(companyData, termsAndConditions.id);

        // Revalidates userInfo data. The protected route logic will take care of the routing navigation
        userInfoMutations.mutateUserInfo();
      } catch {
        throw new Error('Failed to create company');
      }
    } catch {
      toast.error('There was a problem creating your company');
      setError('Failed to create organisation. Please try again.');
    }
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountry(e.target.value as LegalEntityCountry);
    // Clear company-related states when country changes
    setCompanySearch('');
    setCompanyResults(null);
    setSelectedCompany(null);
    // Clear manual entry fields
    setManualCompanyName('');
    setManualCompanyType('');
    setManualRegistrationNo('');
    setManualStreet('');
    setManualCity('');
    setManualCounty('');
    setManualPostalCode('');
  };

  const toggleTermsAndConditionsCheckbox = () => {
    setHasAgreedToTerms(!hasAgreedToTerms);
  };

  return (
    <div>
      <h2 className="mb-10 text-3xl font-bold">Your Organisation</h2>
      {error && (
        <div className="relative mb-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      <div className="mb-9 flex items-center gap-4">
        {authenticatedInfo?.activeOrg?.logoUrl && (
          <img
            src={authenticatedInfo?.activeOrg?.logoUrl}
            alt=""
            loading="eager"
            fetchPriority="high"
            style={{ maxHeight: '60px' }}
          />
        )}
        <p className="text-2xl text-gray-600">{authenticatedInfo?.activeOrg?.name}</p>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="country" className="mb-1 block text-sm font-medium text-gray-700">
            Select Country
          </label>
          <select
            id="country"
            value={country}
            onChange={handleCountryChange}
            className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            required
          >
            <option value="">Select a country</option>
            <option value={LegalEntityCountry.GB}>🇬🇧 United Kingdom</option>
            <option value={LegalEntityCountry.IE}>🇮🇪 Ireland</option>
            {allowGermanyCompanyCreation && <option value={LegalEntityCountry.DE}>🇩🇪 Germany</option>}
          </select>
        </div>

        {requiresCompanyLookup ? (
          <>
            <div className="mb-4">
              <label htmlFor="companySearch" className="mb-1 block text-sm font-medium text-gray-700">
                Search Registered Company Name
              </label>
              <input
                type="text"
                id="companySearch"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Start typing to search for your company"
              />
            </div>

            {isSearching && <p className="mb-4 text-sm text-gray-500">Searching...</p>}

            {!isSearching && companyResults?.length === 0 && (
              <p className="mb-4 text-sm text-gray-500">No results found. Please try a different search.</p>
            )}

            {!isSearching && companyResults && companyResults.length > 0 && (
              <div className="mb-4">
                <label htmlFor="companySelect" className="mb-1 block text-sm font-medium text-gray-700">
                  Select Your Company
                </label>
                <select
                  id="companySelect"
                  value={selectedCompany?.legalEntity.registrationNo || ''}
                  onChange={(e) => {
                    const selected = companyResults.find(
                      (company) => company.legalEntity.registrationNo === e.target.value,
                    );
                    setSelectedCompany(selected || null);
                  }}
                  className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select a company</option>
                  {companyResults.map((company) => (
                    <option key={company.legalEntity.registrationNo} value={company.legalEntity.registrationNo}>
                      {company.legalEntity.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {selectedCompany && (
              <div className="mb-4 rounded-md bg-gray-100 p-3">
                <h3 className="font-semibold">Selected Company Details:</h3>
                <p className="text-gray-700">{selectedCompany.legalEntity.name}</p>
                <p className="text-sm text-gray-600">Registration: {selectedCompany.legalEntity.registrationNo}</p>
                <div className="mt-2">
                  <p className="text-sm text-gray-600">{selectedCompany.registeredAddress.street}</p>
                  {selectedCompany.registeredAddress.city && (
                    <p className="text-sm text-gray-600">{selectedCompany.registeredAddress.city}</p>
                  )}
                  {selectedCompany.registeredAddress.county && (
                    <p className="text-sm text-gray-600">{selectedCompany.registeredAddress.county}</p>
                  )}
                  <p className="text-sm text-gray-600">{selectedCompany.registeredAddress.postalCode}</p>
                  <p className="text-sm text-gray-600">{selectedCompany.registeredAddress.country}</p>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  <span className="font-medium">Type:</span> {selectedCompany.legalEntity.type}
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="mb-4">
              <label htmlFor="manualCompanyName" className="mb-1 block text-sm font-medium text-gray-700">
                Company Name *
              </label>
              <input
                type="text"
                id="manualCompanyName"
                value={manualCompanyName}
                onChange={(e) => setManualCompanyName(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter your company name"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="manualCompanyType" className="mb-1 block text-sm font-medium text-gray-700">
                Company Type *
              </label>
              <input
                type="text"
                id="manualCompanyType"
                value={manualCompanyType}
                onChange={(e) => setManualCompanyType(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., GmbH, AG"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="manualRegistrationNo" className="mb-1 block text-sm font-medium text-gray-700">
                Registration Number *
              </label>
              <input
                type="text"
                id="manualRegistrationNo"
                value={manualRegistrationNo}
                onChange={(e) => setManualRegistrationNo(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter registration number"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="manualStreet" className="mb-1 block text-sm font-medium text-gray-700">
                Street Address *
              </label>
              <input
                type="text"
                id="manualStreet"
                value={manualStreet}
                onChange={(e) => setManualStreet(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter street address"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="manualCity" className="mb-1 block text-sm font-medium text-gray-700">
                City *
              </label>
              <input
                type="text"
                id="manualCity"
                value={manualCity}
                onChange={(e) => setManualCity(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter city"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="manualCounty" className="mb-1 block text-sm font-medium text-gray-700">
                County / State
              </label>
              <input
                type="text"
                id="manualCounty"
                value={manualCounty}
                onChange={(e) => setManualCounty(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter county or state (optional)"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="manualPostalCode" className="mb-1 block text-sm font-medium text-gray-700">
                Postal Code *
              </label>
              <input
                type="text"
                id="manualPostalCode"
                value={manualPostalCode}
                onChange={(e) => setManualPostalCode(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter postal code"
                required
              />
            </div>
          </>
        )}

        <Inline align="start" wrap="nowrap" className="mb-3">
          <Checkbox onCheckedChange={toggleTermsAndConditionsCheckbox} className="mt-0.5" />

          <p className="text-sm font-medium text-gray-700">
            By checking this box, I confirm that I have read and agree to Seapoint's{' '}
            <a
              href={termsAndConditionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4"
            >
              Terms of Service
            </a>
          </p>
        </Inline>

        <button
          type="submit"
          className="w-full cursor-pointer rounded bg-primary px-4 py-2 font-bold text-black transition duration-300 ease-in-out hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-40"
          disabled={isContinueDisabled}
        >
          Continue
        </button>
      </form>
    </div>
  );
};

export default CreateCompany;
